import React, { useState, useEffect } from "react";
import { track } from "./firebase.js";
import HomeScreen from "./HomeScreen";
import LoginScreen from "./LoginScreen";
import CharacterSetupScreen from "./CharacterSetupScreen";
import { GoogleOAuthProvider } from "@react-oauth/google";
import axios from "axios";

const CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;
const API_URL = process.env.REACT_APP_API_URL;

function AppContainer() {
  const [user, setUser] = useState(null);
  const [signupInfo, setSignupInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // 토큰 갱신 함수
  const refreshAccessToken = async () => {
    const refreshToken = localStorage.getItem("refreshToken");
    if (!refreshToken) {
      throw new Error("No refresh token available");
    }
    try {
      const res = await axios.post(`${API_URL}/auth/token`, { refreshToken });
      const newAccessToken = res.data.token;
      localStorage.setItem("accessToken", newAccessToken);
      return newAccessToken;
    } catch (error) {
      console.error(
        "Token refresh error:",
        error.response?.data || error.message
      );
      throw error;
    }
  };

  // 프로필 조회 함수 (토큰 갱신 포함)
  const fetchProfile = async (token) => {
    try {
      const res = await axios.get(`${API_URL}/profile/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return res.data.result;
    } catch (error) {
      if (error.response?.status === 401) {
        // 토큰 만료 시 갱신 시도
        const newToken = await refreshAccessToken();
        const res = await axios.get(`${API_URL}/profile/me`, {
          headers: { Authorization: `Bearer ${newToken}` },
        });
        return res.data.result;
      }
      throw error;
    }
  };

  // 마운트 시 로컬스토리지 확인 및 프로필 조회
  useEffect(() => {
    track("app_boot");
    const token = localStorage.getItem("accessToken");
    const storedUser = localStorage.getItem("user");

    if (token && storedUser) {
      const account = JSON.parse(storedUser);
      fetchProfile(token)
        .then((profile) => {
          console.log("Profile fetched:", profile);
          setUser({
            ...account,
            profile,
          });
          setIsLoading(false);
        })
        .catch((error) => {
          console.error(
            "Profile fetch error:",
            error.response?.data || error.message
          );
          localStorage.removeItem("accessToken");
          localStorage.removeItem("refreshToken");
          localStorage.removeItem("user");
          setIsLoading(false);
        });
    } else {
      setIsLoading(false);
    }
  }, []);

  // 로그인/회원가입 완료 콜백
  const handleAuthSuccess = ({
    user,
    isNewUser,
    signupToken,
    refreshToken,
  }) => {
    console.log("handleAuthSuccess - user:", user);
    if (isNewUser) {
      setSignupInfo({ user, signupToken });
      setIsLoading(false);
    } else {
      (
        // signupToken 과 refreshToken 을 바로 사용하도록 고칩니다.
        (localStorage.setItem("accessToken", signupToken))
      );
      localStorage.setItem("refreshToken", refreshToken);
      localStorage.setItem("user", JSON.stringify(user));

      // user.profile이 이미 포함되어 있다면 바로 사용
      if (user.profile) {
        setUser(user);
        setIsLoading(false);
      } else {
        fetchProfile(signupToken)
          .then((profile) => {
            setUser({
              ...user,
              profile,
            });
            setIsLoading(false);
          })
          .catch((error) => {
            console.error(
              "Profile fetch error:",
              error.response?.data || error.message
            );
            setUser(user); // 프로필 조회 실패 시 user 유지
            setIsLoading(false);
          });
      }
    }
  };

  return (
    <GoogleOAuthProvider clientId={CLIENT_ID}>
      {isLoading ? (
        <div style={{ textAlign: "center", padding: "20px" }}>로딩 중...</div>
      ) : signupInfo ? (
        <CharacterSetupScreen
          user={signupInfo.user}
          signupToken={signupInfo.signupToken}
          onMount={() =>
            track("screen_view", { screen_name: "CharacterSetup" })
          }
          onComplete={({ user, accessToken, refreshToken }) => {
            // accessToken · refreshToken · user 모두 저장
            localStorage.setItem("accessToken", accessToken);
            localStorage.setItem("refreshToken", refreshToken);
            localStorage.setItem("user", JSON.stringify(user));
            setUser(user);
            setSignupInfo(null);
          }}
        />
      ) : user ? (
            <HomeScreen
              user={user}
              onMount={() => track("screen_view", { screen_name: "Home" })}
            />
      ) : (

              <LoginScreen
                onAuthSuccess={handleAuthSuccess}
                onMount={() => track("screen_view", { screen_name: "Login" })}
              />
      )}
    </GoogleOAuthProvider>
  );
}

export default AppContainer;